import {
	ChangeDetectorRef,
	Component,
	HostListener,
	OnInit,
	Injector,
	QueryList,
	ViewChild,
	ViewChildren,
	AfterViewInit,
	OnDestroy
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent, Event } from '@angular/router';

import { IonMenu, IonRouterOutlet, MenuController, NavController, Platform } from '@ionic/angular';
import { register } from 'swiper/element/bundle';

import { AhsTranslationService } from '@nx-ahs/base-translate';
import { TimeoutService } from '@nx-ahs/timeout';
import { SecureMessagingService } from '@nx-ahs/secure-messaging';

import { Subscription } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { MailService } from './core/services/mail.service';
import { SecurityService } from './core/services/security.service';
import { AhsSplashService } from './core/services/ahs-splash.service';
import { AppCoreService } from './core/services/app-core.service';
import { MenuConfigService } from './core/services/menu-config.service';
import { MenuItem } from './shared/models/client/menu-item';
import { environment } from '../environments/environment';

import version from '../../../../version.json';
import { LanguageService } from './core/services/language.service';
import { PORTAL_PAGE_NAMES } from './shared/languages/registeredPortalPageNames';
import { HelpService } from './core/services/help.service';

import { FormlyService, FormlyOptionsService } from '@nx-ahs/formly/shared';
import { Five9ChatService } from './core/services/five9-chat.service';
declare let gtag: (command: string, ...args: any[]) => void;

// swiper register, only called once here for application
register();

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
	standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(IonMenu)
	ionMenu: IonMenu;

	@ViewChild(IonRouterOutlet, { static: false }) routerOutlet: IonRouterOutlet;

	@ViewChild('Message')
	message: any;

	@ViewChild('menuitemlist') menuitemlist: any;

	@ViewChildren('menuItem') menuItems: QueryList<any>;

	@HostListener('window:resize')
	onResize(): void {
		if (this.helpService.showTutorial.getValue()) this.updatePositions();
	}

	// this is useful for figuring out 508 issues
	//@HostListener('document: focusin', ['$event']) focuschange(event: any) {
	//	console.log('Focus is: ', document.activeElement);
	//}

	public selectedIndex = 0;
	public initComplete: boolean = false;
	public showChatFab: boolean = false;
	public isLive = true;
	public isDBReady = true;

	public routerOutletLabel: string = '';
	public appName: string = '';
	public appVersion: string = 'Build Core';
	public appEnv: string = null;
	public appClientLogoImg: string;
	public appPages: MenuItem[];

	private googleAnalyticsId: string = '';
	private isGoogleAnalyticsEnabled: boolean = false;
	private timeoutSubscription: Subscription;
	private unreadLetterSubscription: Subscription;
	private unreadMessageCountSubscription: Subscription;

	private introUserChangedSubscription: Subscription;
	private changePasswordUserChangedSubscription: Subscription;
	private showTutorialSubscription: Subscription;
	private countDownDialog: MatDialogRef<any>;

	private dbTranslateSubscription: Subscription = null;

	constructor(
		private activatedRoute: ActivatedRoute,
		private ahsTranslationService: AhsTranslationService,
		private languageService: LanguageService,
		private appCoreService: AppCoreService,
		private dialogService: MatDialog,
		private mailSvc: MailService,
		private menuConfigService: MenuConfigService,
		private menuCtrl: MenuController,
		private navCtrl: NavController,
		private platform: Platform,
		private router: Router,
		private secureMessagingService: SecureMessagingService,
		private securityService: SecurityService,
		private splashService: AhsSplashService,
		private timeoutService: TimeoutService,
		private changeDetectorRef: ChangeDetectorRef,
		private helpService: HelpService,
		private formlyService: FormlyService,
		private injector: Injector,
		private five9ChatService: Five9ChatService
	) {
		this.initializeApp();
	}

	initializeApp() {
		this.platform.ready().then(() => {});
		this.logOutAllTabsOnTokenClear();
	}

	/**
	 * These three functions are for keyboard navigation within the app between menu items and the router outlet.
	 */
	public focusOnRouterOutlet() {
		this.updateRouterOutletLabel();
		this.routerOutlet.nativeEl.focus();
	}

	public leftOnRouterOutlet() {
		if (document.activeElement === this.routerOutlet.nativeEl) {
			this.menuitemlist.el.focus();
		}
	}

	public rightOnMenuItemList() {
		if (document.activeElement === this.menuitemlist.el) {
			this.focusOnRouterOutlet();
		}
	}

	private updateRouterOutletLabel(): void {
		const pages = this.routerOutlet?.nativeEl?.querySelectorAll('.ion-page');
		const activePage = pages?.length ? pages[pages.length - 1] : null;
		const ionTitle = activePage?.querySelector('ion-title');
		this.routerOutletLabel = ionTitle?.textContent?.trim() || '';
	}

	private updateRouterOutletLabelFromElement(pageEl: HTMLElement): void {
		const ionTitle = pageEl.querySelector('ion-title');
		const text = ionTitle?.textContent?.trim();
		if (text) {
			this.routerOutletLabel = text;
			return;
		}

		// Title not rendered yet (translation pipe pending) — observe until it appears
		const observer = new MutationObserver(() => {
			const title = pageEl.querySelector('ion-title')?.textContent?.trim();
			if (title) {
				this.routerOutletLabel = title;
				observer.disconnect();
			}
		});
		observer.observe(pageEl, { childList: true, subtree: true, characterData: true });

		// Safety: disconnect after 5 seconds if title never appears
		setTimeout(() => observer.disconnect(), 5000);
	}

	/**
	 * Whenever the token is cleared out from local storage, call the logout method so all active tabs
	 * are logged out.
	 * @returns void
	 */
	private logOutAllTabsOnTokenClear(): void {
		window.addEventListener('storage', (event) => {
			if (event.storageArea == localStorage) {
				const token = localStorage.getItem('ahs-localToken');
				if (token == null) {
					this.securityService.logout();
				}
			}
		});
	}

	/**
	 * Determines if translations can be loaded immediately, or if a subscription is neccesary.
	 * If session is authenticated, load translations now.
	 * If session is not authenticated, (WIP)load public translations now, then load protected translations when user logs in.
	 */
	private async determineTranslationLoadMethod(): Promise<void> {
		if (!this.securityService.isAuthenticated()) {
			// Do not load all translations until user has logged in. This is to help page load speed on non-logged in pages, like login and verification.
			this.dbTranslateSubscription = this.securityService.currentUserChanged.subscribe(async (userLoggedIn) => {
				await this.loadDatabaseTranslations();
			});
		} else {
			await this.loadDatabaseTranslations();
		}
		return;
	}

	/**
	 * Loads all translations by each configured page if session is authenciated.
	 * If session is not authenticated, we do nothing.
	 * If there was a subscription set up for this call, we unsubcribe after everything is loaded. This gurantees we only load once.
	 */
	private async loadDatabaseTranslations(): Promise<void> {
		if (this.securityService.isAuthenticated()) {
			const languages2 = await this.languageService.getLanguagesForPages([...PORTAL_PAGE_NAMES]).toPromise();
			languages2.forEach(async (lang) => {
				this.ahsTranslationService.loadProtectedTranslations(...lang.locales);
			});
			this.ahsTranslationService.protectedTranslationsLoaded.emit();

			this.languageService.loadingLanguagesFinished.emit(true);
			this.languageService.isInitialLanguageLoadFinished = true;

			if (this.dbTranslateSubscription != null) {
				this.dbTranslateSubscription.unsubscribe();
			}
		}
		return;
	}

	private setBrowserPageTitle(title: string): void {
		document.title = title;
	}

	/**
	 * Google Analytics is opt-in per contract. A contract's client config must have a
	 * gaMeasurementId(Production|Test) set to a valid GA4 id - if it's null (e.g. Massachusetts,
	 * which was never set up for GA), no script is loaded and no gtag calls are made.
	 */
	private initializeGoogleAnalytics(): void {
		this.googleAnalyticsId =
			environment.environmentName == 'Production'
				? this.appCoreService.clientConfig?.appComponent?.gaMeasurementIdProduction
				: this.appCoreService.clientConfig?.appComponent?.gaMeasurementIdTest;

		if (!this.googleAnalyticsId) {
			return;
		}

		const gtagScript = document.createElement('script');
		gtagScript.async = true;
		gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.googleAnalyticsId}`;
		document.head.appendChild(gtagScript);

		(window as any).dataLayer = (window as any).dataLayer || [];
		(window as any).gtag = function (...args: any[]) {
			(window as any).dataLayer.push(args);
		};
		gtag('js', new Date());
		gtag('config', this.googleAnalyticsId, {
			send_page_view: false
		});

		this.isGoogleAnalyticsEnabled = true;
	}

	async ngOnInit(): Promise<void> {
		this.isLive = this.appCoreService.isLive;
		this.isDBReady = this.appCoreService.isDBReady;
		this.initializeFormly();
		await this.splashService.show(false, 30);

		if (this.isLive) {
			this.appName = this.appCoreService.clientConfig?.publicLayout?.clientPortalName;
			this.appClientLogoImg = this.appCoreService.clientConfig?.appComponent?.clientLogoImg;

			this.setBrowserPageTitle(this.appName);

			this.appVersion = version.version;
			if (environment.environmentName != 'Production') {
				this.appEnv = environment.environmentName;
			}

			await this.determineTranslationLoadMethod();

			// Disable back swipe gesture, this just makes things seem broken. Mainly on login
			this.routerOutlet.swipeGesture = false;

			// the live chat is loaded here if it is active. The options for the chat are in the config
			if (this.appCoreService.clientConfig?.five9LiveChat?.isActive) {
				await this.loadLiveChat();
				this.five9ChatService.showChatFab$.subscribe((show) => {
					this.showChatFab = show;
				});
			}

			await this.menuCtrl.enable(false);
			await this.determineIfMenuShows();
			this.appPages = this.menuConfigService.menuItemSubset;
			this.menuConfigService.tabMenuChanged.subscribe((really) => {
				if (really) {
					this.appPages = this.menuConfigService.menuItemSubset;
				}
			});

			this.loadTranslations();
			this.ahsTranslationService.onLangChange.subscribe((data) => {
				this.loadTranslations();
			});

			this.initializeGoogleAnalytics();

			const path = window.location.pathname.split('folder/')[1];
			if (path !== undefined) {
				this.selectedIndex = this.appPages.findIndex((page) => page.title.toLowerCase() === path.toLowerCase());
			}

			this.router.events
				.pipe(filter((e: Event | RouterEvent): e is RouterEvent => e instanceof RouterEvent))
				.subscribe((event: RouterEvent) => {
					if (event instanceof NavigationEnd && this.isGoogleAnalyticsEnabled) {
						// remove query params before trying to determine page title
						const urlPath: string = event.urlAfterRedirects.split('?')[0];
						const pageTitle: string = this.determinePageTitle(urlPath);
						gtag('event', 'page_view', {
							page_title: pageTitle,
							page_path: event.urlAfterRedirects,
							send_to: this.googleAnalyticsId
						});
					}
				});

			//Do stuff on route change
			this.router.events
				.pipe(
					filter((event) => event instanceof NavigationEnd),
					map(() => this.activatedRoute),
					map((route) => {
						while (route.firstChild) route = route.firstChild;

						return route;
					}),
					filter((route) => route.outlet === 'primary'),
					mergeMap((route) => route.data)
				)
				.subscribe(async () => {
					await this.handleRouteChanges();
				});

			this.timeoutSubscription = this.timeoutService.setupTimeoutSubscription(this.dialogService);

			if (!this.unreadLetterSubscription) {
				this.unreadLetterSubscription = this.mailSvc.unreadLetterCount.subscribe(
					(data) => {
						this.checkForUnreadLetters(data);
					},
					(error) => {
						console.error(error);
					},
					() => {}
				);
			}

			if (!this.unreadMessageCountSubscription) {
				this.unreadMessageCountSubscription = this.secureMessagingService.unreadMessageCount.subscribe(
					(data) => {
						this.checkForUnreadMessages(data);
					},
					(error) => {
						console.error(error);
					},
					() => {}
				);
			}

			/*
			|| If logged in, get number of letters sent since last time user looked at the E-Notices page.
			||   mailSvc.getNoticeCountSinceLastAccess will emit unreadLetterCount subscription.  This same
			||   call is made in login page to get letter count for initial display of page.
			||	Also gets unread message count.
			*/
			if (this.securityService.isAuthenticated()) {
				this.mailSvc.getNoticeCountSinceLastAccess();
				this.secureMessagingService.getUnreadMessageCount();
			}

			this.introUserChangedSubscription = this.securityService.currentUserChanged.subscribe(
				async (userLoggedIn) => {
					if (userLoggedIn && this.appCoreService.clientConfig?.helpMenu?.showTutorialOnLogin)
						this.helpService.showTutorial.next(true);
				}
			);

			// if user refreshes the page after logging in, send them to the change password page if they have a temporary password.
			if (this.securityService.isAuthenticated() && this.securityService.getCurrentUser()?.isPasswordTemporary) {
				this.goTo('/settings/changePassword');
			}
			// after logging in , check if the user has a temporary password.  If so, send them to change password page.
			this.changePasswordUserChangedSubscription = this.securityService.currentUserChanged.subscribe((data) => {
				if (this.securityService.isAuthenticated() && data && data?.isPasswordTemporary) {
					this.goTo('/settings/changePassword');
				}
			});
		}
	}

	ngAfterViewInit() {
		this.initComplete = true;
		this.splashService.hide(1);
		// Subscribe to showTutorial and update positions if true
		this.showTutorialSubscription = this.helpService.showTutorial.subscribe((show: boolean) => {
			if (show) {
				this.updatePositions();
			}
		});
		this.changeDetectorRef.detectChanges();
	}

	ngOnDestroy() {
		if (this.timeoutSubscription) {
			this.timeoutSubscription.unsubscribe();
		}

		if (this.unreadLetterSubscription) {
			this.unreadLetterSubscription.unsubscribe();
		}

		if (this.introUserChangedSubscription) {
			this.introUserChangedSubscription.unsubscribe();
		}

		if (this.changePasswordUserChangedSubscription) {
			this.changePasswordUserChangedSubscription.unsubscribe();
		}

		if (this.showTutorialSubscription) {
			this.showTutorialSubscription.unsubscribe();
		}
	}

	public mobileChatButton(): void {
		this.five9ChatService.maximizeChat();
	}

	public skipToMain(event: MouseEvent | KeyboardEvent): void {
		// A link with an href only activates on Enter natively; Space is routed here via (keydown.space).
		event.preventDefault();
		event.stopPropagation();

		const mainContent = document.getElementById('main-content');
		if (!mainContent) {
			return;
		}

		// Enrollment wizard: focus the step heading (e.g. "Step 2 Member Information") so the screen reader
		// reads the heading and then the instructions that follow, rather than jumping past them to the
		// first field. The heading isn't natively focusable, so give it a programmatic tabindex.
		const stepHeading = mainContent.querySelector<HTMLElement>('.wizard-content .current-step-title');
		if (stepHeading?.checkVisibility({ visibilityProperty: true })) {
			if (!stepHeading.hasAttribute('tabindex')) {
				stepHeading.setAttribute('tabindex', '-1');
			}
			stepHeading.focus();
			stepHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
			return;
		}

		// Other pages (e.g. landing list's "click here to enroll" link): first visible focusable
		// in the routed page. Cached Ionic pages are display:none, so they're skipped.
		// The landing layout puts a header (logo/link) before #content, so scope to #content to skip it.
		const contentAreas = Array.from(mainContent.querySelectorAll<HTMLElement>('#content')).filter((el) =>
			el.checkVisibility({ visibilityProperty: true })
		);
		const target = this.findFirstFocusable(contentAreas[0] ?? mainContent);
		if (target) {
			target.focus();
			target.scrollIntoView({ behavior: 'smooth', block: 'center' });
			return;
		}

		mainContent.focus();
		mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	private findFirstFocusable(container: HTMLElement): HTMLElement | null {
		const selector = [
			'a[href]',
			'button:not([disabled])',
			'input:not([disabled]):not([type="hidden"])',
			'select:not([disabled])',
			'textarea:not([disabled])',
			'[tabindex]:not([tabindex="-1"])'
		].join(',');

		return (
			Array.from(container.querySelectorAll<HTMLElement>(selector)).find((el) =>
				el.checkVisibility({ visibilityProperty: true })
			) ?? null
		);
	}

	private updatePositions(): void {
		this.menuItems.forEach((directive, index) => {
			const element = directive.el;
			const position = element.getBoundingClientRect();
			const key = this.appPages[index].url;
			this.helpService.setMenuItemPosition(key, position);
		});
	}

	private loadLiveChat(): Promise<void> {
		const five9Config = this.appCoreService.clientConfig.five9LiveChat;

		// Five9's newer Chat Wrapper integration (F9.Chat.Wrapper.init) uses a different
		// script and API than the legacy Social Widget integration below, so it's loaded
		// and initialized separately based on the client config.
		if (five9Config.integrationType === 'wrapper') {
			return this.loadFive9ChatWrapper(five9Config);
		}

		// legacy Five9 Social Widget (e.g. MO)
		return new Promise<void>((resolve, reject) => {
			const el = document.createElement('script');
			el.type = 'text/javascript';
			el.src = five9Config.libraryUrl;
			el.onload = () => resolve();
			el.onerror = () => reject(new Error(`Failed to load Five9 library: ${el.src}`));
			document.body.appendChild(el);
		}).then(() => {
			this.five9ChatService.initialize();
		});
	}

	// newer Five9 Chat Wrapper (e.g. OH)
	private loadFive9ChatWrapper(five9Config: any): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			const el = document.createElement('script');
			el.type = 'text/javascript';
			el.src = five9Config.libraryUrl;
			el.onload = () => resolve();
			el.onerror = () => reject(new Error(`Failed to load Five9 chat wrapper: ${el.src}`));
			document.head.appendChild(el);
		}).then(() => {
			this.five9ChatService.initializeWrapper(five9Config.options, five9Config.visibleRoutes);
		});
	}

	private logOutUser(): void {
		this.securityService.logout();
	}

	private checkForUnreadLetters(unreadLetterCount: number): void {
		const letterIdx = this.appPages
			.map(function (ap) {
				return ap.tabName.toLowerCase();
			})
			.indexOf('e-notices');

		if (letterIdx != -1) {
			this.appPages[letterIdx].mobileShowOverlay = unreadLetterCount > 0;
			this.appPages[letterIdx].layerCount = unreadLetterCount;
		}
	}

	private checkForUnreadMessages(unreadMessageCount: number): void {
		const messageIdx = this.appPages
			.map(function (ap) {
				return ap.tabName.toLowerCase();
			})
			.indexOf('secure-messaging');

		if (messageIdx != -1) {
			this.appPages[messageIdx].mobileShowOverlay = unreadMessageCount > 0;
			this.appPages[messageIdx].layerCount = unreadMessageCount;
		}
	}

	// TODO: make this use the translations
	private determinePageTitle(url: string): string {
		let pageTitle: string = '';

		switch (url) {
			case '/help/faq':
				pageTitle = 'Frequently Asked Questions';
				break;
			case '/menu/application':
				pageTitle = 'My Application';
				break;
			case '/menu/document':
				pageTitle = 'My Documents';
				break;
			case '/menu/e-notices':
				pageTitle = 'My E-Notices';
				break;
			case '/menu/help':
				pageTitle = 'Help';
				break;
			case '/menu/home':
				pageTitle = 'My Family';
				break;
			case '/menu/language':
				pageTitle = 'Change Language';
				break;
			case '/menu/payment':
				pageTitle = 'My Payments';
				break;
			case '/menu/secure-messaging':
				pageTitle = 'Message Us';
				break;
			case '/menu/enrollment':
				pageTitle = 'My Enrollment';
				break;
			case '/menu/settings':
				pageTitle = 'Account Preferences';
				break;
			case 'menu/home/personDetails':
				pageTitle = 'Person Details';
				break;
			case '/security/forgot-password':
				pageTitle = 'Forgot Password';
				break;
			case '/security/confirm-account':
				pageTitle = 'Confirm Account';
				break;
			case '/security/login':
				pageTitle = 'Login';
				break;
			case '/security/register':
				pageTitle = 'Register';
				break;
			case '/security/reset-password':
				pageTitle = 'Reset Password';
				break;
			case '/settings/accountInformation':
				pageTitle = 'Account Information';
				break;
			case '/settings/changePassword':
				pageTitle = 'Change Password';
				break;
			case '/settings/notificationPreferences':
				pageTitle = 'Notification Preferences';
				break;
			case '/settings/accountSettings':
				pageTitle = 'Account Settings';
				break;
			case '/settings/maintenance':
				pageTitle = 'Maintenance';
				break;
			default:
				pageTitle = 'Portal';
		}

		return pageTitle;
	}

	public goTo(url: string, routerDirection: string = 'forward', closeMenu: boolean = true): void {
		if (routerDirection == 'back') {
			this.navCtrl.navigateBack(url);
		} else if (routerDirection == 'forward') {
			this.navCtrl.navigateForward(url);
		} else {
			this.navCtrl.navigateRoot(url);
		}

		if (closeMenu) {
			this.ionMenu.close();
		}
	}

	private async handleRouteChanges(): Promise<void> {
		const data = this.getMergedActiveRouteData();

		await this.determineIfMenuShows();

		if (!data?.ignoreSplash) {
			await this.splashService.show();
		} else {
			this.splashService.hide(1);
		}

		this.timeoutService.actionTaken();

		// Update router outlet label for screen readers after the new page renders
		const pages = this.routerOutlet?.nativeEl?.querySelectorAll('.ion-page');
		const enteringPage = pages?.length ? (pages[pages.length - 1] as HTMLElement) : null;
		if (enteringPage) {
			this.updateRouterOutletLabelFromElement(enteringPage);
		}
	}

	private async determineIfMenuShows() {
		const merged = {
			...this.getMergedActiveRouteData()
		};

		const hide = !!merged?.ignoreMenu;

		await this.menuCtrl.enable(!hide);
		if (hide) await this.menuCtrl.close();
	}

	private loadTranslations(): void {
		let letterCount: number = 0;
		let messageCount: number = 0;
		const letterIdx = this.appPages
			.map(function (ap) {
				return ap.tabName.toLowerCase();
			})
			.indexOf('e-notices');
		if (letterIdx != -1 && Object.prototype.hasOwnProperty.call(this.appPages[letterIdx], 'layerCount')) {
			letterCount = this.appPages[letterIdx].layerCount;
		}
		const messageIdx = this.appPages
			.map(function (ap) {
				return ap.tabName.toLowerCase();
			})
			.indexOf('secure-messaging');
		if (messageIdx != -1 && Object.prototype.hasOwnProperty.call(this.appPages[messageIdx], 'layerCount')) {
			messageCount = this.appPages[messageIdx].layerCount;
		}

		this.appPages = [];
		this.appPages = this.menuConfigService.getMenuItemSubset();
		this.checkForUnreadLetters(letterCount);
		this.checkForUnreadMessages(messageCount);
	}

	private initializeFormly(): void {
		const formlyOptions = new FormlyOptionsService();
		formlyOptions.apiUrl = environment.apiUrl;
		formlyOptions.localTokenName = 'adal.idtoken';
		formlyOptions.injector = this.injector;
		formlyOptions.clientConfig = this.appCoreService?.clientConfig;

		this.formlyService.init(formlyOptions);
	}

	/** Merge route.data across the active snapshot chain (handles lazy children). */
	private getMergedActiveRouteData(): any {
		let data: any = {};
		// climb to root
		let r = this.activatedRoute;
		while (r.parent) r = r.parent;
		// walk down, merging data at each level
		while (r) {
			if (r.snapshot?.data) data = { ...data, ...r.snapshot.data };
			r = r.firstChild!;
		}
		return data;
	}
}
